import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InferRequestType } from "hono/client";
import { api, unwrap } from "../api.ts";
import { useBrand, useGroups, useMe } from "../hooks.ts";
import { Badge, Button, Card, Dialog, ErrorText, Field, Input, PageHeader, Select, Spinner } from "../components/ui.tsx";
import { ImpersonateDialog } from "../components/impersonation.tsx";

type Patch = InferRequestType<(typeof api.admin.users)[":id"]["$patch"]>["json"];

const ago = (d: string | null) => {
  if (!d) return "Never";
  const s = (Date.now() - new Date(d).getTime()) / 1000;
  if (s < 60) return "Just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(d).toLocaleDateString();
};

export function UsersPage() {
  const brand = useBrand();
  const qc = useQueryClient();
  const me = useMe();
  const users = useQuery({ queryKey: ["users"], queryFn: () => unwrap(api.admin.users.$get()) });
  const groups = useGroups();
  const [q, setQ] = useState("");
  const [inviting, setInviting] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [viewingAs, setViewingAs] = useState<{ id: string; name: string; email: string; admin: boolean } | null>(null);
  const [rowError, setRowError] = useState<unknown>(null);

  const patch = useMutation({
    mutationFn: ({ id, json }: { id: string; json: Patch }) => unwrap(api.admin.users[":id"].$patch({ param: { id }, json })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
    onError: setRowError,
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.admin.users[":id"].$delete({ param: { id } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
    onError: setRowError,
  });
  const signOut = useMutation({
    mutationFn: (u: { id: string; email: string }) => unwrap(api.admin.users[":id"]["sign-out"].$post({ param: { id: u.id } })).then((r) => ({ ...r, email: u.email })),
    onSuccess: (r) => setNotice({ ok: true, text: r.sessions ? `Signed ${r.email} out on ${r.sessions === 1 ? "1 device" : `${r.sessions} devices`}. They sign in again to continue.` : `${r.email} wasn't signed in anywhere.` }),
    onError: setRowError,
  });

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (users.data ?? []).filter((u) => !needle || u.email.includes(needle) || u.name.toLowerCase().includes(needle));
  }, [users.data, q]);

  return (
    <>
      <PageHeader
        title="Users"
        description="Add people, assign them to a group, and control who is an admin."
        actions={<Button variant="primary" onClick={() => setInviting(true)}>Add user</Button>}
      />
      <div className="mb-4 flex items-center gap-3">
        <Input placeholder="Search by name or email" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
        <span className="text-sm text-[var(--muted)]">{users.data?.length ?? 0} users</span>
      </div>
      {notice && (
        <Card className={`mb-3 p-3 text-sm ${notice.ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}>
          {notice.text} <button className="ml-2 text-xs underline" onClick={() => setNotice(null)}>dismiss</button>
        </Card>
      )}
      <ErrorText error={rowError} />
      <Card className="mt-3 overflow-x-auto">
        {users.isPending ? (
          <div className="grid place-items-center py-16"><Spinner /></div>
        ) : (
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-[var(--line)] bg-[var(--panel-2)]/40 text-left text-[12.5px] font-medium whitespace-nowrap text-[var(--muted)]">
                <th className="px-4 py-3 font-medium">User</th>
                <th className="px-4 py-3 font-medium">Group</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Last sign-in</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => {
                const self = u.id === me.data?.user.id;
                const set = (json: Patch) => {
                  setRowError(null);
                  patch.mutate({ id: u.id, json });
                };
                return (
                  <tr key={u.id} className="border-b border-[var(--line)] last:border-0">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {u.image ? <img src={u.image} alt="" className="h-8 w-8 rounded-full" /> : <div className="grid h-8 w-8 place-items-center rounded-full bg-[var(--panel-2)] text-xs font-semibold">{u.email[0]?.toUpperCase()}</div>}
                        <div className="min-w-0">
                          <div className="truncate font-medium">
                            {u.name} {self && <Badge>you</Badge>}{" "}
                            {u.superAdmin && (
                              <span title="The first person who signed in. Only they can open the raw memory inspector, which shows everyone's private memory.">
                                <Badge tone="accent">super admin</Badge>
                              </span>
                            )}
                          </div>
                          <div className="truncate text-xs text-[var(--muted)]">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Select value={u.groupId ?? ""} onChange={(e) => set({ groupId: e.target.value || null })} className="h-8 w-40">
                        <option value="">No group</option>
                        {groups.data?.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <Select value={u.role} onChange={(e) => set({ role: e.target.value as "admin" | "user" })} className="h-8 w-28">
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <button disabled={self} onClick={() => set({ status: u.status === "active" ? "disabled" : "active" })} title={self ? "" : "Click to toggle"}>
                        <Badge tone={u.status === "active" ? "good" : "bad"}>{u.status}</Badge>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-[var(--muted)]">{u.lastLoginAt ? ago(u.lastLoginAt) : <Badge>Invited</Badge>}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        {!self && u.status === "active" && (
                          <Button variant="ghost" className="h-8 whitespace-nowrap" title="See their account as they see it" onClick={() => setViewingAs({ id: u.id, name: u.name, email: u.email, admin: u.role === "admin" })}>
                            View as
                          </Button>
                        )}
                        {!self && u.lastLoginAt && (
                          <Button variant="ghost" className="h-8 whitespace-nowrap" title="End their sign-ins on every device" onClick={() => confirm(`Sign ${u.email} out on every device?`) && signOut.mutate(u)}>
                            Sign out
                          </Button>
                        )}
                        {!self && (
                          <Button variant="ghost" className="h-8" onClick={() => confirm(`Remove ${u.email} from ${brand.name}?${u.superAdmin ? " They are the super admin: nobody will be able to open the memory inspector afterwards." : ""}`) && remove.mutate(u.id)}>
                            Remove
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
      <InviteDialog open={inviting} onClose={() => setInviting(false)} onAdded={setNotice} />
      <ImpersonateDialog user={viewingAs} onClose={() => setViewingAs(null)} />
    </>
  );
}

type Notice = { ok: boolean; text: string };

function inviteNotice(r: { email: string; invitationEmail?: string; invitationError?: string }): Notice {
  if (r.invitationEmail === "sent") return { ok: true, text: `Added ${r.email} and emailed them an invitation.` };
  if (r.invitationEmail === "failed") return { ok: false, text: `Added ${r.email}, but the invitation email couldn't be sent (${r.invitationError ?? "unknown error"}). Let them know they can sign in.` };
  return { ok: true, text: `Added ${r.email}. Email isn't set up on this install, so let them know they can sign in.` };
}

function InviteDialog({ open, onClose, onAdded }: { open: boolean; onClose: () => void; onAdded: (n: Notice) => void }) {
  const qc = useQueryClient();
  const groups = useGroups();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [groupId, setGroupId] = useState("");
  const [role, setRole] = useState<"user" | "admin">("user");
  const invite = useMutation({
    mutationFn: () => unwrap(api.admin.users.$post({ json: { email, name, role, groupId: groupId || null, status: "active" } })),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["users"] });
      onAdded(inviteNotice(r));
      setEmail("");
      setName("");
      onClose();
    },
  });
  return (
    <Dialog open={open} onClose={onClose} title="Add user">
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); invite.mutate(); }}>
        <Field label="Work email" hint="They sign in with this address through Google or Microsoft. We email them an invitation.">
          <Input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" />
        </Field>
        <Field label="Name (optional)">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Group">
            <Select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <option value="">Default group</option>
              {groups.data?.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </Select>
          </Field>
          <Field label="Role">
            <Select value={role} onChange={(e) => setRole(e.target.value as "user" | "admin")}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </Select>
          </Field>
        </div>
        <ErrorText error={invite.error} />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={invite.isPending}>Add user</Button>
        </div>
      </form>
    </Dialog>
  );
}
